import { useEffect, useState } from "react";
import { Power, Save, Play, AlertTriangle, Loader2, FlaskConical } from "lucide-react";
import {
  adminGetDunningSettings,
  adminUpdateDunningSettings,
  adminRunDunningNow,
} from "../../lib/api";

function NumberField({ label, value, onChange, min, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70 mb-1">{label}</label>
      <input
        type="number"
        min={min}
        value={value ?? ""}
        onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        data-testid={testid}
        className="w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm"
      />
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70 mb-1">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm"
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows = 5, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-xs font-mono"
      />
    </div>
  );
}

export default function BillingAutomationPanel({ onToast }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setS(await adminGetDunningSettings());
      } catch (e) {
        onToast?.(e?.response?.data?.detail || "Failed to load settings", "error");
      }
      setLoading(false);
    })();
  }, [onToast]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (s.suspend_after_days >= s.final_notice_after_days || s.final_notice_after_days >= s.delete_after_days) {
      onToast?.("Day thresholds must be: suspend < final notice < delete", "error");
      return;
    }
    setSaving(true);
    try {
      const updated = await adminUpdateDunningSettings(s);
      setS(updated);
      onToast?.("Saved");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Save failed", "error");
    }
    setSaving(false);
  };

  const run = async (dryRun) => {
    const label = dryRun ? "DRY-RUN (no changes will be made)" : "REAL RUN — this will suspend / delete / email customers";
    if (!window.confirm(`Run one dunning cycle now?\n\nMode: ${label}\n\nContinue?`)) return;
    setRunning(true);
    setLastRun(null);
    try {
      const r = await adminRunDunningNow(dryRun);
      setLastRun(r);
      if (r.skipped) {
        onToast?.(r.reason || "Skipped", "error");
      } else {
        const s = r.summary || {};
        onToast?.(`${dryRun ? "DRY: " : ""}Suspended ${s.suspended || 0}, Notices ${s.final_notice_sent || 0}, Deleted ${s.deleted || 0}, Errors ${s.errors || 0}`);
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Run failed", "error");
    }
    setRunning(false);
  };

  if (loading || !s) return <div className="p-8 text-[#11151C]/60">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-2xl font-extrabold text-[#11151C] inline-flex items-center gap-2">
          <Power className="w-6 h-6 text-[#FF6B3D]" />
          Billing Automation (Dunning)
        </h2>
        <p className="text-sm text-[#11151C]/65 mt-1">
          Automatically suspend → warn → delete hosting accounts when invoices go overdue. Disabled by default. Always test in DRY-RUN first.
        </p>
      </div>

      {/* Master toggles */}
      <div className="p-5 rounded-2xl border border-[#FF6B3D]/30 bg-[#FFFBF6]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-extrabold text-[#11151C] inline-flex items-center gap-2">
              <Power className={`w-5 h-5 ${s.enabled ? "text-green-600" : "text-[#11151C]/40"}`} />
              Master switch
            </h3>
            <p className="text-xs text-[#11151C]/60 mt-1">When OFF, the cron + manual runs do nothing.</p>
          </div>
          <button
            onClick={() => set("enabled", !s.enabled)}
            data-testid="dunning-toggle-enabled"
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${s.enabled ? "bg-green-500" : "bg-gray-300"}`}
          >
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${s.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="font-extrabold text-[#11151C] inline-flex items-center gap-2">
              <FlaskConical className={`w-5 h-5 ${s.dry_run ? "text-blue-600" : "text-[#11151C]/40"}`} />
              Dry-run mode
            </h3>
            <p className="text-xs text-[#11151C]/60 mt-1">Logs what WOULD happen — no real suspend/email/delete. Recommended for first runs.</p>
          </div>
          <button
            onClick={() => set("dry_run", !s.dry_run)}
            data-testid="dunning-toggle-dryrun"
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${s.dry_run ? "bg-blue-500" : "bg-gray-300"}`}
          >
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${s.dry_run ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
      </div>

      {/* Day thresholds */}
      <div className="p-5 rounded-2xl border border-[#11151C]/10 bg-white">
        <h3 className="font-extrabold text-[#11151C] mb-1">Stage thresholds</h3>
        <p className="text-xs text-[#11151C]/60 mb-4">Days overdue (after `expires_at`) when each action triggers. Must be strictly ascending.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <NumberField label="Suspend after (days)" value={s.suspend_after_days} onChange={(v) => set("suspend_after_days", v)} min={1} testid="dunning-suspend-after-days" />
          <NumberField label="Final notice after (days)" value={s.final_notice_after_days} onChange={(v) => set("final_notice_after_days", v)} min={2} testid="dunning-final-notice-after-days" />
          <NumberField label="Delete after (days)" value={s.delete_after_days} onChange={(v) => set("delete_after_days", v)} min={3} testid="dunning-delete-after-days" />
        </div>
      </div>

      {/* Email templates */}
      <div className="p-5 rounded-2xl border border-[#11151C]/10 bg-white">
        <h3 className="font-extrabold text-[#11151C] mb-1">Email templates</h3>
        <p className="text-xs text-[#11151C]/60 mb-4">
          Placeholders: <code>{"{customer_name}"}</code>, <code>{"{domain}"}</code>, <code>{"{days_overdue}"}</code>, <code>{"{portal_url}"}</code>
        </p>
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Suspend — subject" value={s.suspend_subject} onChange={(v) => set("suspend_subject", v)} testid="dunning-suspend-subject" />
            <TextField label="Final notice — subject" value={s.final_notice_subject} onChange={(v) => set("final_notice_subject", v)} testid="dunning-final-notice-subject" />
          </div>
          <TextAreaField label="Suspend — body" value={s.suspend_body} onChange={(v) => set("suspend_body", v)} testid="dunning-suspend-body" />
          <TextAreaField label="Final notice — body" value={s.final_notice_body} onChange={(v) => set("final_notice_body", v)} testid="dunning-final-notice-body" />
          <TextField label="Deletion — subject" value={s.deletion_subject} onChange={(v) => set("deletion_subject", v)} testid="dunning-deletion-subject" />
          <TextAreaField label="Deletion — body" value={s.deletion_body} onChange={(v) => set("deletion_body", v)} testid="dunning-deletion-body" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="dunning-save"
          className="lm-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save settings
        </button>
        <button
          onClick={() => run(true)}
          disabled={running || !s.enabled}
          data-testid="dunning-run-dry"
          title={!s.enabled ? "Enable the master switch first" : ""}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Run now (DRY)
        </button>
        <button
          onClick={() => run(false)}
          disabled={running || !s.enabled || s.dry_run}
          data-testid="dunning-run-real"
          title={s.dry_run ? "Switch off Dry-run to actually run" : ""}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
        >
          <Play className="w-4 h-4" />
          Run now (REAL)
        </button>
      </div>

      {/* Last run output */}
      {lastRun && (
        <div className="p-5 rounded-2xl border border-[#11151C]/10 bg-[#FFFBF6]">
          <h3 className="font-extrabold text-[#11151C] mb-2 inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#FF6B3D]" />
            Last run output
          </h3>
          <pre className="text-[11px] text-[#11151C]/75 overflow-x-auto bg-white rounded-lg p-3 border border-[#11151C]/10 max-h-80">
            {JSON.stringify(lastRun, null, 2)}
          </pre>
        </div>
      )}

      {/* Cron note */}
      <div className="text-xs text-[#11151C]/55 italic">
        Daily cron endpoint: <code>POST {process.env.REACT_APP_BACKEND_URL}/api/cron/dunning</code> with header <code>X-Cron-Secret: $CRON_SECRET</code>
      </div>
    </div>
  );
}
